import Arrow from "../../assets/img/arrow-left-grey-round.svg";
import useTranslator from "../../hooks/useTranslator";
import DownloadIcon from "../../assets/img/downloadIcon.svg";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  calculateOverlayScore,
  getUserDisplayName,
  getCustomIncomeDetails,
  getFieldForPolicies,
  getRentPercentage,
  haveValue,
  tenantHasMissingDniNie,
} from "../../helper/common";
import LandLordContext from "../../context/landlord/LandLordContext";
import TenantContext from "../../context/tenant/TenantContext";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/en-gb";
import AuthContext from "../../context/auth/AuthContext";
import Loader from "../../components/Loader";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";

const ReportOverview = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const { getPropertyDetails, getTenantExtraData, deaWorkspace } =
    useContext(LandLordContext);
  const { user } = useContext(AuthContext);
  const { getZipOfMultipleTenantDocs } = useContext(TenantContext);
  const queryParameters = new URLSearchParams(window.location.search);
  const property_id = queryParameters.get("property");
  const sharedprofileid = queryParameters.getAll("sharedprofileid");
  const rentVal = queryParameters.get("rent");
  const redirectUrl = queryParameters.get("redirectUrl");
  const selectionRaw = queryParameters.get("selection");

  const selectionSelectedIds = useMemo(() => {
    if (!selectionRaw) return null;
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return model.flatMap((entry) => {
        if (entry.type === "tenant") return [entry.tenantId].filter(Boolean);
        return (entry.participants || [])
          .map((p) => p.tenantId)
          .filter(Boolean);
      });
    } catch {
      return null;
    }
  }, [selectionRaw]);

  const selectionSelectedGuarantorIds = useMemo(() => {
    if (!selectionRaw) return null;
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return model.flatMap((entry) => {
        if (entry.type === "tenant") return entry.guarantorIds || [];
        return (entry.participants || []).flatMap((p) => p.guarantorIds || []);
      });
    } catch {
      return null;
    }
  }, [selectionRaw]);

  const todaysDate = (locale = localStorage.getItem("i18nextLng")) => {
    moment.locale(locale);
    let currentDate = moment().format("D MMMM YYYY");
    return currentDate;
  };
  const STATUS_STYLES = {
    approved: {
      backgroundColor: "#F2FBE9",
      color: "#82CF3F",
      border: ".0694vw solid #DAECC8",
      text: _t("approved"),
      img: "/images/tick-new.svg",
      insurable: _t("insurable"),
    },
    review: {
      backgroundColor: "#FFFBEC",
      color: "#E1BB20",
      border: ".0694vw solid #F2E09B",
      text: _t("manualReview"),
      img: "/images/warning.svg",
      insurable: _t("insurable"),
    },
    rejected: {
      backgroundColor: "#FFE2E2",
      color: "#FF3C3C",
      border: ".0694vw solid #EB9B9C",
      text: _t("declined"),
      img: "/images/notApproved.svg",
      insurable: _t("notInsurable"),
    },
  };

  const [status, setStatus] = useState("approved");
  const [addressVal, setAddressVal] = useState("");
  const [tenantData, setTenantData] = useState([]);
  const [showLocalLoading, setShowLocalLoading] = useState(false);
  const [individualError, setIndividualError] = useState(false);
  const [individualWarnings, setIndividualWarnings] = useState(false);
  const [lowScoreCheck, setLowScoreCheck] = useState(false);
  const [higherScoreProfile, setHigherScoreProfile] = useState(false);
  const [missingDniNieWarning, setMissingDniNieWarning] = useState({});
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    if (haveValue(property_id)) {
      getPropertyDetails(property_id)
        .then((res) => {
          const data = res.properties;
          setAddressVal(data);
        })
        .catch((err) => {
          console.log(err);
        });
    }

    if (haveValue(sharedprofileid)) {
      setShowLocalLoading(true);
      getTenantExtraData(sharedprofileid)
        .then(async (res) => {
          await handleTenantResponse(res?.success_res);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          console.log("Finally ran");
          setShowLocalLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    const result = tenantData.reduce(
      (acc, item) => {
        if (item?.errorEntries?.length > 0) {
          acc.isHardFail = true;
          acc.errorsByLabel[item.label] = item.errorEntries;
        }

        if (item?.vals?.DOCUMENTATION > 0) {
          acc.warningsByLabel[item.label] = item?.vals?.MISSING_DOCS;
        }

        if (item?.hasMissingDniNie) {
          acc.missingDniNieByLabel[item.label] = true;
        }
        return acc;
      },
      {
        isHardFail: false,
        errorsByLabel: {},
        warningsByLabel: {},
        missingDniNieByLabel: {},
      }
    );
    setMissingDniNieWarning(result?.missingDniNieByLabel);
    console.log(result, "checkResult");
    setIndividualError(result?.errorsByLabel);
    setIndividualWarnings(result?.warningsByLabel);
    const highPointProfile = tenantData.reduce((max, current) => {
      if (!max) return current;

      return current?.DEA_SCORE?.finalScore > max?.DEA_SCORE?.finalScore
        ? current
        : max;
    }, null);
    const profilesHaveMissingDocs = tenantData.some(
      (item) => item?.vals?.DOCUMENTATION > 0
    );

    if (highPointProfile?.DEA_SCORE?.finalScore <= 4) {
      setLowScoreCheck(true);
    }

    if (result?.isHardFail) {
      setStatus("rejected");
    } else if (
      highPointProfile?.DEA_SCORE?.finalScore > 4 &&
      !profilesHaveMissingDocs
    ) {
      setStatus("approved");
    } else {
      setStatus("review");
    }

    setHigherScoreProfile(highPointProfile);
  }, [tenantData]);

  const handleGoBack = () => {
    if (redirectUrl) {
      navigate(redirectUrl);
    } else {
      navigate(-1);
    }
  };

  const downloadPdfAndDocuments = async () => {
    const container = document.getElementById("divToPrint");

    try {
      setShowSpinner(true);

      const A4_WIDTH = 595.28;
      const A4_HEIGHT = 841.89;

      // hide buttons
      const hiddenElements = document.querySelectorAll(".no-print");
      hiddenElements.forEach((el) => (el.style.display = "none"));

      const pdf = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: "a4",
      });

      const canvas = await html2canvas(container, {
        useCORS: true,
        backgroundColor: "#ffffff",
        scale: 2,
        ignoreElements: (element) => element.classList.contains("no-print"),
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const scale = Math.min(A4_WIDTH / imgWidth, A4_HEIGHT / imgHeight);

      const renderWidth = imgWidth * scale;
      const renderHeight = imgHeight * scale;

      const x = (A4_WIDTH - renderWidth) / 2;
      const y = (A4_HEIGHT - renderHeight) / 2;

      pdf.addImage(imgData, "JPEG", x, y, renderWidth, renderHeight);

      // convert PDF to blob instead of downloading
      const pdfBlob = pdf.output("blob");

      hiddenElements.forEach((el) => (el.style.display = ""));

      // -----------------------------
      // GET BACKEND ZIP
      // -----------------------------

      const allTenants = tenantData?.map((tenant) => tenant?._id);

      const res = await getZipOfMultipleTenantDocs(allTenants);

      const backendZipBlob = res.data;

      // -----------------------------
      // UNZIP BACKEND ZIP
      // -----------------------------

      const backendZip = await JSZip.loadAsync(backendZipBlob);

      // create final zip
      const finalZip = new JSZip();

      // add generated pdf
      finalZip.file("report_and_documents.pdf", pdfBlob);

      // add backend files
      const promises = [];

      backendZip.forEach((relativePath, file) => {
        if (!file.dir) {
          promises.push(
            file.async("blob").then((content) => {
              finalZip.file(relativePath, content);
            })
          );
        }
      });

      await Promise.all(promises);

      // -----------------------------
      // GENERATE FINAL ZIP
      // -----------------------------

      const finalZipBlob = await finalZip.generateAsync({
        type: "blob",
      });

      const url = window.URL.createObjectURL(finalZipBlob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "documents.zip";

      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      setShowSpinner(false);
    } catch (err) {
      console.error("Error capturing element:", err);
      setShowSpinner(false);
    }
  };

  const handleTenantResponse = async (response) => {
    let userIncomeObj = {};
    let tenantIncomeSum = 0;
    let guarantorIncomeSum = 0;

    // ---- Calculate Tenant + Guarantor Income ----
    for (let tenant of response) {
      if (selectionSelectedIds && !selectionSelectedIds.includes(tenant?._id))
        continue;
      const incomeSelection = tenant?.income_selection;

      // Tenant income
      const tenantIncome = getCustomIncomeDetails(
        tenant,
        user,
        _t,
        incomeSelection?.tenant_source,
        undefined,
        undefined,
        deaWorkspace,
        tenant?.custom_income_sources
      );

      const tenantAmount = tenantIncome?.amount ?? 0;
      userIncomeObj[tenant?._id] = tenantAmount;
      tenantIncomeSum += tenantAmount;

      // Guarantor incomes
      let guarantors = tenant?.gurantor_id || [];
      if (selectionSelectedGuarantorIds) {
        guarantors = guarantors.filter((g) =>
          selectionSelectedGuarantorIds.includes(g?._id)
        );
      }
      const guarantorIncomeSelection =
        incomeSelection?.[tenant?._id]?.guarantor_sources ??
        incomeSelection?.guarantor_sources;

      for (let g of guarantors) {
        const guarantorIncome = getCustomIncomeDetails(
          g,
          user,
          _t,
          guarantorIncomeSelection?.[g?._id],
          undefined,
          undefined,
          deaWorkspace,
          tenant?.custom_income_sources
        );

        userIncomeObj[g?._id] = guarantorIncome?.amount ?? 0;
        guarantorIncomeSum += guarantorIncome?.amount ?? 0;
      }
    }

    // Total income from tenant + guarantors
    const totalIncome = tenantIncomeSum + guarantorIncomeSum;

    const finalEffortRate =
      getRentPercentage(totalIncome, rentVal)?.roundValue ?? "0";

    const result = [];

    for (let idx = 0; idx < response.length; idx++) {
      const tenant = response[idx];
      if (selectionSelectedIds && !selectionSelectedIds.includes(tenant?._id))
        continue;
      const tenantSharedProfileId = sharedprofileid[idx];

      // ----- TENANT PROCESSING -----
      const temp = await getFieldForPolicies(
        tenant,
        user,
        finalEffortRate,
        deaWorkspace
      );

      const DEA_SCORE = calculateOverlayScore(
        temp.EFFORT_RATE_SCORE,
        temp.TENURE,
        temp.EMPLOYMENT_PREDICTABILITY,
        temp.SECTOR_CYCLICALITY,
        temp.EMPLOYER_STABILITY
      );

      const errorEntries = Object.entries(temp).filter(
        ([key, value]) => value?.isHardFail === true
      );

      let guarantorOutsideSpain = false;
      let checkGuarantors = tenant?.gurantor_id || [];
      if (selectionSelectedGuarantorIds) {
        checkGuarantors = checkGuarantors.filter((g) =>
          selectionSelectedGuarantorIds.includes(g?._id)
        );
      }
      for (let g of checkGuarantors) {
        const forensicData = g?.tenant_extra_field_id?.forensic_financial_data;
        if (forensicData) {
          for (let k in forensicData) {
            if (
              forensicData[k]?.origin &&
              forensicData[k]?.origin !== "Local"
            ) {
              guarantorOutsideSpain = true;
              break;
            }
          }
        }
        if (guarantorOutsideSpain) break;
      }
      if (guarantorOutsideSpain) {
        errorEntries.push([
          "GUARANTOR_OUTSIDE_SPAIN",
          { isHardFail: true, label: "guarantorLivingOutsideSpain" },
        ]);
      }

      const label = getUserDisplayName(tenant);

      tenant.label = label;

      result.push({
        ...tenant,
        role: "tenant",
        sharedProfileId: tenantSharedProfileId,
        DEA_SCORE,
        errorEntries,
        vals: temp,
        income: userIncomeObj?.[tenant?._id],
        hasMissingDniNie: tenantHasMissingDniNie(tenant),
      });

      // ----- GUARANTOR PROCESSING (same as tenant) -----
      let guarantors = tenant?.gurantor_id || [];
      if (selectionSelectedGuarantorIds) {
        guarantors = guarantors.filter((g) =>
          selectionSelectedGuarantorIds.includes(g?._id)
        );
      }

      for (let g of guarantors) {
        const gTemp = await getFieldForPolicies(
          g,
          user,
          finalEffortRate,
          deaWorkspace
        );

        const gDEA_SCORE = calculateOverlayScore(
          gTemp.EFFORT_RATE_SCORE,
          gTemp.TENURE,
          gTemp.EMPLOYMENT_PREDICTABILITY,
          gTemp.SECTOR_CYCLICALITY,
          gTemp.EMPLOYER_STABILITY
        );

        const gErrorEntries = Object.entries(gTemp).filter(
          ([key, value]) => value?.isHardFail === true
        );

        const gLabel = getUserDisplayName(g);

        g.label = gLabel;

        result.push({
          ...g,
          role: "guarantor",
          sharedProfileId: tenantSharedProfileId,
          parentTenantId: tenant?._id,
          DEA_SCORE: gDEA_SCORE,
          errorEntries: gErrorEntries,
          vals: gTemp,
          income: userIncomeObj?.[g?._id],
          hasMissingDniNie: tenantHasMissingDniNie(g),
        });
      }
    }

    setTenantData(result);
  };

  const goToReportPage = () => [
    navigate(
      `/${workspaceId}/generatedReport/combinedReport${window.location.search}`
    ),
  ];

  const handleProfileClick = (tenant) => {
    const baseUrl = `/${workspaceId}/profile-property?sharedprofileid=${tenant.sharedProfileId}`;
    const url =
      tenant.role === "tenant"
        ? `${baseUrl}&tenant_id=${tenant._id}`
        : `${baseUrl}&guarantor=${tenant._id}`;
    window.open(url, "_blank");
  };

  const formatMissingDoc = (val) => {
    const payslipMatch = val.match(/^payslip\s*-\s*(.+)$/i);
    if (payslipMatch) {
      return `${_t("Payslip")} - ${payslipMatch[1]}`;
    }
    return _t(val);
  };

  return showLocalLoading ? (
    <Loader />
  ) : (
    <div className="report-overview-comp" id="divToPrint">
      {/* Header */}
      <div className="report-overview__header">
        <div
          className="report-overview__back cursor-pointer no-print"
          onClick={handleGoBack}
        >
          <img src={Arrow} alt="" />
          <span>{_t("goBack")}</span>
        </div>

        <h1 className="report-overview__title">
          {[
            `${addressVal?.street ?? ""} ${
              addressVal?.street_number ?? ""
            }`.trim(),
            `${addressVal?.floor_number ? addressVal?.floor_number : ""} ${
              addressVal?.flat_number ? "-" + addressVal?.flat_number : ""
            }`,

            addressVal?.province,
          ]
            .filter(Boolean)
            .join(", ")}
        </h1>

        <div className="report-overview__subinfo">
          <span>
            {[addressVal?.postcode, addressVal?.muncipality]
              .filter(Boolean)
              .join(", ")}
          </span>
          <span className="report-overview__dot">•</span>
          <span>€{rentVal}/month</span>
        </div>

        <div className="report-overview__meta">
          <div className="report-overview__meta-item">
            <img src={"/images/calendar-icon.svg"} alt="" />
            <span>{todaysDate()}</span>
          </div>
          <div className="report-overview__meta-item">
            <img src={"/images/multiple-user.svg"} alt="" />{" "}
            <span>
              {" "}
              {sharedprofileid?.length} {_t("tenantsSelected")}
            </span>
          </div>
        </div>
      </div>

      {/* Approval Card */}
      <div
        style={STATUS_STYLES[status]}
        className="report-overview__approval-card"
      >
        <div className="report-overview__approval-status">
          <img src={STATUS_STYLES[status].img} />
          <span style={{ color: STATUS_STYLES[status].color }}>
            {STATUS_STYLES[status].text}
          </span>
        </div>

        <div className="report-overview__approval-metrics">
          <div>
            <strong>
              {_t("finalScore")}: {higherScoreProfile?.DEA_SCORE?.finalScore}
            </strong>{" "}
            / 10
          </div>

          <div>
            <strong>
              {_t("combinedEffortRate")}:{" "}
              {tenantData[0]?.vals?.EFFORT_RATE_SCORE?.EFFORT_RATE_SCORE}%
            </strong>
          </div>

          <div className="report-overview__insurable">
            {tenantData?.some(
              (item) => item?.vals?.DEFAULT_HISTORY?.isHardFail === true
            ) ? (
              <img src={"/images/not-insurable.svg"} alt="" />
            ) : (
              <img src={"/images/shield.svg"} alt="" />
            )}
            <span
              style={{
                color: tenantData?.some(
                  (item) => item?.vals?.DEFAULT_HISTORY?.isHardFail === true
                )
                  ? "#FF3C3C"
                  : "#82CF3F",
              }}
            >
              {_t(
                tenantData?.some(
                  (item) => item?.vals?.DEFAULT_HISTORY?.isHardFail === true
                )
                  ? "notInsurable"
                  : "insurable"
              )}
            </span>
          </div>
        </div>

        <p className="report-overview__approval-text">
          {_t(
            status === "rejected"
              ? "oneOrMoreTenantsDoNotMeetTheRequiredRligibilityCriteriaForThisRental"
              : status === "review"
                ? "someTenantsRequireAdditionalVerificationBeforeTheRentalCanBeApproved"
                : "allSelectedTenantsMeetTheEligibilityCriteriaAndQualifyForInsuranceCoverage"
          )}
        </p>
        {(status === "rejected" || status === "review") && (
          <div className="report-overview-declined-decision">
            <h1>{_t("whyThisDecision")}?</h1>
            <ul
              style={{
                "--bullet-color": STATUS_STYLES[status].color,
              }}
            >
              {Object?.entries(individualError)?.map(([name, errors]) =>
                errors.map(([_, value], index) => (
                  <li key={`${name}-${index}`}>
                    {name} {_t("has")} {_t(value?.label)}
                  </li>
                ))
              )}
              {lowScoreCheck && (
                <li key={`scoreissue`}>{_t("collectiveScoreIsLessThan4")}</li>
              )}
              {Object?.entries(individualWarnings)?.map(
                ([name, docsMissing]) => (
                  // warnings.map(([_, value], index) => (
                  <li key={`${name}`}>
                    {name} {_t("has")} {docsMissing?.length ?? 0}{" "}
                    {_t("missingDocuments")}
                  </li>
                )
                // ))
              )}
              {Object?.entries(missingDniNieWarning)?.map(([name]) => (
                <li key={`${name}-dni`}>
                  {name}: {_t("missingDniOrNie")}
                </li>
              ))}
            </ul>
            {/* {status === "rejected" && (
              <button>{_t("reviewFlaggedTenant")}</button>
            )} */}
          </div>
        )}
      </div>

      {/* Individual Tenant Scores */}
      <h2 className="report-overview__section-title">
        {_t("individualTenantScores")}
      </h2>
      {tenantData?.map((tenant) => (
        <div className="report-overview__tenant-card-comp">
          <div className="report-overview__tenant-card">
            <div className="report-overview__tenant-header">
              <div
                style={{
                  backgroundColor: STATUS_STYLES["approved"].backgroundColor,
                  color: STATUS_STYLES["approved"].color,
                }}
                className="report-overview__avatar"
              >
                {tenant?.label?.substring(0, 1)}
              </div>

              <div className="report-overview__tenant-info">
                <h3
                  className={"d-flex align-items-center gap-1 cursor-pointer"}
                  onClick={() => handleProfileClick(tenant)}
                >
                  <span>{tenant?.label ?? ""}</span>
                  <span
                    style={{ fontSize: "0.833vw", lineHeight: "normal" }}
                    className="neutral"
                  >
                    {_t(tenant?.role)}
                  </span>
                  <img
                    src="/images/external-link.svg"
                    alt=""
                    className="no-print"
                    style={{
                      width: "0.833vw",
                      height: "0.833vw",
                      marginLeft: "0.347vw",
                      verticalAlign: "middle",
                    }}
                  />
                </h3>
                <div className="report-overview__tenant-meta">
                  {haveValue(tenant?.paymentScores?.creditScore?.value) && (
                    <>
                      <span>
                        {_t("skor")}:{" "}
                        {tenant?.paymentScores?.creditScore?.value}
                      </span>
                      <span>•</span>
                    </>
                  )}
                  <span>
                    {_t("incomeVal")}: {tenant?.income}
                  </span>
                  <span>•</span>
                  <span
                    style={{
                      color:
                        tenant?.vals?.DEFAULT_HISTORY?.isHardFail === true
                          ? "#E1BB20"
                          : "#82CF3F",
                      backgroundColor:
                        tenant?.vals?.DEFAULT_HISTORY?.isHardFail === true
                          ? "#FFFBEC"
                          : "#F2FBE9",
                    }}
                    className="report-overview__badge"
                  >
                    {
                      <>
                        {tenant?.vals?.DEFAULT_HISTORY?.isHardFail === true ? (
                          ""
                        ) : (
                          <img src={"/images/shield.svg"} alt="" />
                        )}

                        {_t(
                          tenant?.vals?.DEFAULT_HISTORY?.isHardFail === true
                            ? "notInsurable"
                            : "insurable"
                        )}
                      </>
                    }
                  </span>
                </div>
              </div>

              <div className="report-overview__score">
                <div className="report-overview__score-value">
                  {tenant?.DEA_SCORE?.finalScore}
                </div>
                <div className="report-overview__score-label">
                  {_t("score")}
                </div>
              </div>
            </div>

            {/* Tenant Details */}
            <div className="report-overview__tenant-details">
              <div className="report-overview__detail-row">
                <span>{_t("debtRatio")}</span>
                <span style={{ color: "#253837" }}>
                  {tenant?.DEA_SCORE?.EFFORT_RATE?.label}{" "}
                </span>
                <b
                  className={
                    tenant?.DEA_SCORE?.EFFORT_RATE?.score > 0
                      ? "plus"
                      : tenant?.DEA_SCORE?.EFFORT_RATE?.score < 0
                        ? "minus"
                        : "neutral"
                  }
                >
                  {tenant?.DEA_SCORE?.EFFORT_RATE?.score > 0 && "+"}
                  {tenant?.DEA_SCORE?.EFFORT_RATE?.score}
                </b>
              </div>

              <div className="report-overview__detail-row">
                <span>{_t("seniority")}</span>
                <span style={{ color: "#253837" }}>
                  {_t(tenant?.DEA_SCORE?.TENURE?.label)}{" "}
                </span>
                <b
                  className={
                    tenant?.DEA_SCORE?.TENURE?.score > 0
                      ? "plus"
                      : tenant?.DEA_SCORE?.TENURE?.score < 0
                        ? "minus"
                        : "neutral"
                  }
                >
                  {tenant?.DEA_SCORE?.TENURE?.score > 0 && "+"}
                  {tenant?.DEA_SCORE?.TENURE?.score}
                </b>
              </div>

              <div className="report-overview__detail-row">
                <span>{_t("incomeType")}</span>
                <span style={{ color: "#253837" }}>
                  {_t(tenant?.DEA_SCORE?.INCOME_TYPE?.label)}{" "}
                </span>
                <b
                  className={
                    tenant?.DEA_SCORE?.INCOME_TYPE?.score > 0
                      ? "plus"
                      : tenant?.DEA_SCORE?.INCOME_TYPE?.score < 0
                        ? "minus"
                        : "neutral"
                  }
                >
                  {tenant?.DEA_SCORE?.INCOME_TYPE?.score > 0 && "+"}
                  {tenant?.DEA_SCORE?.INCOME_TYPE?.score}
                </b>
              </div>

              <div className="report-overview__detail-row">
                <span>{_t("employmentType")}</span>
                <span style={{ color: "#253837" }}>
                  {_t(tenant?.DEA_SCORE?.CONTRACT_TYPE?.label)}{" "}
                </span>
                <b
                  className={
                    tenant?.DEA_SCORE?.CONTRACT_TYPE?.score > 0
                      ? "plus"
                      : tenant?.DEA_SCORE?.CONTRACT_TYPE?.score < 0
                        ? "minus"
                        : "neutral"
                  }
                >
                  {tenant?.DEA_SCORE?.CONTRACT_TYPE?.score > 0 && "+"}
                  {tenant?.DEA_SCORE?.CONTRACT_TYPE?.score}
                </b>
              </div>

              <div
                style={{ borderBottom: "none" }}
                className="report-overview__detail-row"
              >
                <span>{_t("riskSector")}</span>
                <span style={{ color: "#253837" }}>
                  {_t(tenant?.DEA_SCORE?.RISK_SECTORS?.label)}{" "}
                </span>
                <b
                  className={
                    tenant?.DEA_SCORE?.RISK_SECTORS?.score > 0
                      ? "plus"
                      : tenant?.DEA_SCORE?.RISK_SECTORS?.score < 0
                        ? "minus"
                        : "neutral"
                  }
                >
                  {tenant?.DEA_SCORE?.RISK_SECTORS?.score > 0 && "+"}
                  {tenant?.DEA_SCORE?.RISK_SECTORS?.score}
                </b>
              </div>
            </div>

            {haveValue(individualError?.[tenant?.label]) && (
              <div className="report-overview-declined-review-info-comp">
                <div className="report-overview-declined-review-info">
                  <img src="/images/notApproved.svg" alt="" />
                  <div>
                    <h1 style={{ color: STATUS_STYLES[status]?.color }}>
                      {_t("rejected")}
                    </h1>
                    <ul>
                      {individualError?.[tenant?.label]?.map((val) => (
                        <li>{_t(`${val[1]?.label}DescInOverview`)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {haveValue(individualWarnings?.[tenant?.label]) && (
              <div className="report-overview-declined-review-info-comp">
                <div className="report-overview-declined-review-info">
                  <img src="/images/warning.svg" alt="" />
                  <div>
                    <h1 style={{ color: STATUS_STYLES["review"]?.color }}>
                      {_t("missingDocuments")}
                    </h1>
                    <ul>
                      {individualWarnings?.[tenant?.label]?.map((val) => (
                        <li>{formatMissingDoc(val)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
      <div className="report-overview-footer no-print">
        <p>{_t("clickOnAnyTenantToViewDetailedScoringBreakdown")}</p>
        <div>
          <button
            className="btnNext"
            disabled={showSpinner}
            onClick={downloadPdfAndDocuments}
          >
            {!showSpinner && <img src={DownloadIcon} />}
            {showSpinner ? `${_t("downloading")}...` : _t("downloadReport")}
          </button>
          <button className="dark-button" onClick={goToReportPage}>
            {_t("viewFullDetails")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportOverview;
